"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Layers, Droplets, ShieldCheck, ArrowUpRight, Cog, FileText, Gauge, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const capabilities = [{
  id: 1,
  title: "Lubrificação de matrizes",
  description: "Aplicação uniforme de desmoldantes sem excessos ou névoa residual.",
  icon: Droplets
}, {
  id: 2,
  title: "Decapagem de precisão",
  description: "Remoção consistente de carepa com bicos de impacto controlado.",
  icon: Gauge
}, {
  id: 3,
  title: "Arrefecimento de rolos",
  description: "Estabilidade térmica uniforme para proteger perfis e cilindros de laminação.",
  icon: Layers
}, {
  id: 4,
  title: "Tratamento anticorrosivo",
  description: "Revestimento homogéneo de peças estampadas antes do acabamento.",
  icon: ShieldCheck
}];
const highlights = [{
  value: "30+",
  label: "Anos de experiência técnica"
}, {
  value: "100%",
  label: "Uniformidade de aplicação"
}, {
  value: "-40%",
  label: "Consumo de lubrificante"
}];
export default function FabricacaoMetal() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="fabricacaometalhero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      {/* Subtle industrial background pattern */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[radial-gradient(#0f2338_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Hero Copy Column */}
          <motion.div variants={containerVariants} initial="hidden" animate="show" className="lg:col-span-7 flex flex-col items-start gap-6 text-left">
            <motion.div variants={itemVariants} className="flex flex-wrap gap-2 items-center">
              <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                Siderurgia & Estampagem
              </Badge>
              <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                <Cog className="size-3.5 text-secondary animate-spin [animation-duration:8s]" />
                Spraying Systems Co. desde 1993
              </div>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground text-balance">
              Fabricação de metal e estampagem
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Controle a lubrificação de ferramentas, o arrefecimento contínuo de cilindros e a decapagem de carepa com sistemas de pulverização de precisão extrema da Eurocontrol.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3 pt-2 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto shadow-md">
                <Link to="/ContactsPage">
                  <span>Consulte os nossos engenheiros</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/CatalogsPage">
                  <FileText className="size-4 text-secondary" />
                  <span>Ver catálogo de bicos</span>
                </Link>
              </Button>
            </motion.div>

            {/* Quick Metrics Strip */}
            <motion.div variants={itemVariants} className="grid grid-cols-3 gap-4 pt-6 mt-2 border-t border-border w-full max-w-xl">
              {highlights.map((item, idx) => <div key={item.label} data-index={idx} className="flex flex-col">
                  <span className="text-xl sm:text-2xl font-bold font-default text-primary">
                    {item.value}
                  </span>
                  <span className="text-xs text-muted-foreground leading-snug">
                    {item.label}
                  </span>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Right Visual Column */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.55,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-xl">
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/metal_fabrication_spraying.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/metal_fabrication_spraying.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/metal_fabrication_spraying.png-wvc-sizes" alt="Processamento industrial de metal e bicos de pulverização para arrefecimento" loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />
                
                {/* Floating pill indicator */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background/90 backdrop-blur-xs text-foreground text-xs font-medium shadow-sm">
                    <Sparkles className="size-3 text-secondary" />
                    Engenharia de precisão
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-2xs font-mono">
                    ISO 9001
                  </span>
                </div>

                <div className="absolute bottom-4 left-4 right-4 text-primary-foreground">
                  <p className="text-xs font-mono uppercase tracking-wider text-primary-foreground/80">
                    Aplicações siderúrgicas
                  </p>
                  <p className="text-sm sm:text-base font-semibold leading-tight mt-0.5">
                    Lanças, bicos hidráulicos e coletores sob medida
                  </p>
                </div>
              </div>

              {/* Mini Features List below Image */}
              <div className="p-5 bg-card grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div className="flex items-start gap-2.5">
                  <div className="p-1.5 rounded-md bg-accent text-accent-foreground shrink-0 mt-0.5">
                    <Droplets className="size-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-foreground">Dosagem micrométrica</h4>
                    <p className="text-2xs text-muted-foreground">Evita névoa no chão de fábrica</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="p-1.5 rounded-md bg-accent text-accent-foreground shrink-0 mt-0.5">
                    <ShieldCheck className="size-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-foreground">Vida útil estendida</h4>
                    <p className="text-2xs text-muted-foreground">Menor desgaste das matrizes</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Process / Solution Grid */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold font-default text-foreground">
                Sistemas especializados para o setor metalomecânico
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Soluções técnicas configuradas para prensas, fieiras e linhas de decapagem contínua.
              </p>
            </div>
            <Button asChild variant="ghost" size="sm" className="self-start md:self-auto group">
              <Link to="/SteelAndMetalsCaseStudyPage">
                <span>Ver casos de estudo em siderurgia</span>
                <ArrowUpRight className="size-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {capabilities.map((item, idx) => {
            const IconComp = item.icon;
            return <Card key={item.id} data-index={idx} className="bg-card border border-border rounded-xl shadow-xs transition-[border-color,box-shadow] hover:border-secondary/50 hover:shadow-md">
                  <CardContent className="p-5 flex flex-col justify-between h-full gap-4">
                    <div className="flex items-center justify-between">
                      <div className="p-2.5 rounded-lg bg-accent text-accent-foreground">
                        <IconComp className="size-5" />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground">
                        0{idx + 1}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1.5">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}